import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { Chart, ChartConfiguration, ChartDataset, registerables } from "chart.js";

Chart.register(...registerables);

@Component({
	selector: "app-cbta-summary-radar-chart",
	standalone: false,
	templateUrl: "./cbta-summary-radar-chart.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./cbta-summary-radar-chart.component.scss",
})
export class CbtaSummaryRadarChartComponent implements OnDestroy, OnInit, OnChanges {
	@ViewChild("radarChart", { static: true }) radarChart!: ElementRef<HTMLCanvasElement>;
	@Input() labels: string[] = [];
	@Input() datasets: ChartDataset<"radar">[] = [];

	private chart?: Chart;

	ngOnInit(): void {
		setTimeout(() => {
			this.updateChart();
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			(changes["labels"] && !changes["labels"].firstChange) ||
			(changes["datasets"] && !changes["datasets"].firstChange)
		) {
			// Re-render chart when inputs change
			this.updateChart();
		}
	}

	private findIntersectionPoints(): number[] {
		if (this.datasets.length < 2) return [];

		const intersectionPoints: number[] = [];
		const data1 = this.datasets[0].data as number[];
		const data2 = this.datasets[1].data as number[];

		for (let i = 0; i < Math.min(data1.length, data2.length); i++) {
			if (data1[i] === data2[i]) {
				intersectionPoints.push(i);
			}
		}

		return intersectionPoints;
	}

	private colorToHex(color: string): string {
		if (color.startsWith("#")) {
			return color;
		} else if (color.startsWith("rgb")) {
			const matches = color.match(/\d+/g);
			if (matches && matches.length >= 3) {
				const r = parseInt(matches[0]);
				const g = parseInt(matches[1]);
				const b = parseInt(matches[2]);
				return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
			}
		}
		return "#000000"; // fallback
	}

	private drawSplitPoints(chart: Chart): void {
		if (this.datasets.length < 2) return;

		const ctx = chart.ctx;
		const intersectionPoints = this.findIntersectionPoints();

		if (intersectionPoints.length === 0) return;

		const color1 = this.datasets[0].pointBackgroundColor as string;
		const color2 = this.datasets[1].pointBackgroundColor as string;
		const borderColor1 = this.datasets[0].pointBorderColor as string;
		const borderColor2 = this.datasets[1].pointBorderColor as string;

		intersectionPoints.forEach((pointIndex) => {
			// Get the point position from the chart
			const meta1 = chart.getDatasetMeta(0);
			const meta2 = chart.getDatasetMeta(1);

			if (meta1.data[pointIndex] && meta2.data[pointIndex]) {
				const point1 = meta1.data[pointIndex];
				const point2 = meta2.data[pointIndex];

				// Use the position from the first dataset
				const x = point1.x;
				const y = point1.y;
				const radius = 6; // Point radius

				ctx.save();

				// Draw the first half (left/top)
				ctx.beginPath();
				ctx.arc(x, y, radius, -Math.PI / 2, Math.PI / 2);
				ctx.fillStyle = color1;
				ctx.fill();
				ctx.strokeStyle = borderColor1;
				ctx.lineWidth = 2;
				ctx.stroke();

				// Draw the second half (right/bottom)
				ctx.beginPath();
				ctx.arc(x, y, radius, Math.PI / 2, -Math.PI / 2);
				ctx.fillStyle = color2;
				ctx.fill();
				ctx.strokeStyle = borderColor2;
				ctx.lineWidth = 2;
				ctx.stroke();

				ctx.restore();
			}
		});
	}

	private updateChart(): void {
		const ctx = this.radarChart?.nativeElement?.getContext("2d");
		if (!ctx) return;

		// Destroy existing chart if it exists
		if (this.chart) {
			this.chart.destroy();
		}

		const config: ChartConfiguration<"radar"> = {
			type: "radar",
			data: {
				labels: this.labels,
				datasets: this.datasets,
			},
			plugins: [
				{
					id: "splitPointsPlugin",
					afterDatasetsDraw: (chart) => {
						this.drawSplitPoints(chart);
					},
				},
			],
			options: {
				responsive: true,
				maintainAspectRatio: true,
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						backgroundColor: "rgba(0, 0, 0, 0.8)",
						titleColor: "#fff",
						bodyColor: "#fff",
						borderColor: "#666",
						borderWidth: 1,
					},
				},
				scales: {
					r: {
						beginAtZero: true,
						min: 0,
						max: 5,
						grid: {
							color: "rgba(0, 0, 0, 0.1)",
							lineWidth: 1,
							circular: true,
						},
						pointLabels: {
							font: {
								size: 14,
								weight: "bolder",
							},
							color: "#333",
							padding: 15,
						},
						ticks: {
							display: true,
							stepSize: 1,
							font: {
								size: 13,
								weight: "bold",
							},
							color: "#666",
						},
					},
				},
				elements: {
					line: {
						tension: 0,
					},
				},
				interaction: {
					intersect: false,
				},
			},
		};

		this.chart = new Chart(ctx, config);
	}

	ngOnDestroy(): void {
		if (this.chart) {
			this.chart.destroy();
		}
	}
}
